import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { DocumentAddIcon, DocumentIcon } from "@heroicons/react/outline";
import { listTutorSanads } from "../../../../api/endpoints/tutors/sanad";
import AddSanadModal from "../../../../components/modal/views/AddSanadModal";
import AppLogo from "../../../../components/AppLogo";
import ChangeLangButton from "../../../../components/language/ChangeLangButton";

type SanadsInfoProps = {
  onContinue?: () => void;
  prevPage?: () => void;
  showLogo?: boolean;
  showHeader?: boolean;
  submitButtonText?: string;
  stepInfo?: string;
  previousButtonText?: string;
};

export default function SanadsInfo(props: SanadsInfoProps) {
  const LocalBasePath = "page.sanadInfo";
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });
  const {
    onContinue,
    prevPage,
    showLogo,
    showHeader,
    submitButtonText,
    stepInfo,
    previousButtonText,
  } = props;
  const [openForm, setOpenForm] = useState(false);
  const [sanads, setSanads] = useState<{ [key: string]: any }[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSanads();
  }, [openForm]);

  const fetchSanads = async () => {
    const sanadsResponse = await listTutorSanads();
    if (sanadsResponse.isSuccess) {
      setSanads(sanadsResponse.data as []);
    }
  };

  const onPressSave = () => {
    navigate(`/tutor/free-time`);
  };

  return (
    <div className="flex-1 flex flex-col">
      <div className="w-full flex   md:flex-row flex-col justify-between  items-center">
        {!!showLogo && <AppLogo className="w-fit md:mb-0 mb-2" />}
        {!!showLogo && <ChangeLangButton dropDownLanguage={true} />}
      </div>
      <div>
        {showHeader && (
          <div className="pb-8 border-b border-gray-200">
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              {t("recitations")}
            </h2>
          </div>
        )}
      </div>
      {sanads?.length === 0 ? (
        <div className="flex-1 my-8 pt-8">
          <div className="flex flex-col items-center gap-6">
            <div className="flex flex-col items-center gap-1">
              <p className="text-lg leading-6 font-medium text-gray-700">
                {t("noSanad")}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpenForm(true)}
              className="inline-flex justify-between items-center gap-2 py-2 px-2 sm:px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-brandblue-600 hover:bg-brandblue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500 disabled:bg-gray-400"
            >
              <DocumentAddIcon
                className="h-5 w-5 sm:rtl:-mr-1 sm:ltr:-ml-1"
                aria-hidden="true"
              />
              <span className="hidden sm:inline" data-cy="addNewSanad">
                {t("addSanad")}
              </span>
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="mt-8">
            <div className="flex justify-between items-center">
              <p className="text-lg leading-6 font-medium text-gray-900">
                {t("sanad")}
              </p>
              <button
                type="button"
                data-cy="addSanad"
                onClick={() => setOpenForm(true)}
                className="inline-flex justify-between items-center gap-2 py-2 px-2 sm:px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-brandblue-600 hover:bg-brandblue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500 disabled:bg-gray-400"
              >
                <DocumentAddIcon
                  className="h-5 w-5 sm:rtl:-mr-1 sm:ltr:-ml-1"
                  aria-hidden="true"
                />
                <span className="hidden sm:inline">{t("addSanad")}</span>
              </button>
            </div>
          </div>
          <div className="mt-8 flex-1">
            <ul role="list" className="divide-y divide-gray-100" data-cy="sanadsList">
              {sanads?.map((sanad, idx) => (
                <li key={`Recitation-${sanad.id}-${idx}`}>
                  <div className="hover:bg-gray-50">
                    <div className="flex items-center py-4">
                      <div className="min-w-0 flex-1 flex items-center">
                        <div className="min-w-0 flex-1 pe-4 md:grid md:grid-cols-2 md:gap-4">
                          <div className="flex flex-col gap-1">
                            <p className="text-sm font-semibold text-gray-600 truncate">
                              {sanad.telawah.name}
                            </p>
                            <p className="text-sm font-normal text-gray-500">
                              {t(sanad.sanad_type)}
                            </p>
                          </div>
                          <div className="hidden md:block">
                            <div>
                              <p className="text-sm text-gray-500">
                                {t("sanadCertificate")}
                              </p>
                              <a
                                className="mt-2 flex items-center text-sm text-brandblue-600"
                                href={sanad.file_url}
                                target="_blank"
                              >
                                <DocumentIcon
                                  className="flex-shrink-0 ltr:mr-1.5 rtl:ml-1.5 h-5 w-5 text-gray-400"
                                  aria-hidden="true"
                                />
                                {t("preview")}
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
      {!!onContinue && (
        <div className="border-t border-gray-200 py-5 sticky bottom-0 bg-white flex justify-between items-center flex-col-reverse sm:flex-row gap-4">
          <div>
            {previousButtonText && (
              <button
                type="button"
                onClick={() => prevPage!()}
                className="flex justify-center flex-shrink-0 py-2 px-4 border border-gray shadow-sm text-sm font-medium rounded-md text-gray bg-transparent  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500 disabled:bg-gray-400"
              >
                {previousButtonText}
              </button>
            )}
          </div>
          <div className=" flex items-center md:flex-row flex-col gap-4 justify-end">
            <div className="w-full flex md:justify-end justify-center text-sm font-medium text-gray-700">
              {stepInfo}
            </div>
            <button
              onClick={() => onPressSave()}
              type="submit"
              className="flex justify-center flex-shrink-0 py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-brandblue-600 hover:bg-brandblue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500 disabled:bg-gray-400"
            >
              {submitButtonText}
            </button>
          </div>
        </div>
      )}
      <AddSanadModal modalState={openForm} setModalState={setOpenForm} />
    </div>
  );
}
